<svelte:options runes={false} />

<script>
  import { Meteor } from 'meteor/meteor';
  import { Tracker } from 'meteor/tracker';
  import { onDestroy, onMount } from 'svelte';
  import {
    HourBitsCollection,
    InvoiceContributionSummariesCollection,
    InvoiceLedgerAggregatesCollection,
    InvoicesCollection,
    PaymentAttemptItemsCollection,
    PaymentAttemptsCollection,
    ProviderConfigsCollection,
    calculatePaymentQuote,
    contributionRatesForBit,
    dollarsToMinor,
    isInvoicePayable,
    minorToMoney,
  } from '../api/invoices';
  import {
    DEFAULT_OPERATIONAL_CURRENCY,
    normalizeOperationalCurrency,
  } from '../api/currencies';
  import {
    PaymentOperationsSummariesCollection,
    PaymentQuotePoliciesCollection,
    PaymentRiskObservationsCollection,
    STRIPE_FEE_QUOTE_POLICIES,
    UNCONFIGURED_STRIPE_FEE_QUOTE_POLICY_ID,
    ZERO_FEE_QUOTE_POLICY,
    feeQuotePolicyForInvoice,
  } from '../api/payments';
  import {
    OperatorAccountsCollection,
    OperatorMembershipsCollection,
  } from '../api/operators';
  import { ACTIVE_INVOICE_PAGE_SIZE, ProjectsCollection } from '../api/projects';
  import {
    DEFAULT_THEME_ID,
    normalizeThemeId,
  } from '../api/themes';
  import {
    INSTANCE_CONFIG_ID,
    INSTANCE_MODES,
    RuntimeConfigCollection,
  } from '../api/runtime-config';
  import UnderConstructionBanner from './UnderConstructionBanner.svelte';
  import StatusToast from './components/StatusToast.svelte';
  import { parsePayerInvoiceReference } from './payer-link.mjs';
  import {
    openInvoiceDetailSubscription,
    operatorIdentitySubscription,
    operatorInvoiceDetailSubscription,
    operatorInvoicesSubscription,
    subscriptionForRoute,
  } from './route-subscriptions.mjs';
  import { createOverlappingSubscription } from './meteor-subscribe.mjs';
  import {
    ROUTE_STATES,
    resolveRouteState,
    routeKeyFor,
  } from './route-states.mjs';
  import RouteSkeleton from './components/RouteSkeleton.svelte';
  import RouteState from './components/RouteState.svelte';
  import IntroPage, { INTRO_SECTIONS } from './pages/IntroPage.svelte';
  import PaymentAttemptPage from './pages/PaymentAttemptPage.svelte';
  import PayerInvoiceView from './pages/PayerInvoiceView.svelte';
  import SoloHomeEntry from './pages/SoloHomeEntry.svelte';
  import OperatorAccountSettingsPage from './pages/OperatorAccountSettingsPage.svelte';
  import OperatorNewProjectPage from './pages/OperatorNewProjectPage.svelte';
  import OperatorProjectSettingsPage from './pages/OperatorProjectSettingsPage.svelte';
  import HomeInvoiceList from './components/HomeInvoiceList.svelte';
  import InvoiceSummaryCard from './components/InvoiceSummaryCard.svelte';
  import OperatorAccessPanel from './components/OperatorAccessPanel.svelte';
  import OperatorInvoiceDetail from './components/OperatorInvoiceDetail.svelte';

  let path = window.location.pathname;
  let errorMessage = '';
  let noticeMessage = '';
  let invoice = null;
  let invoices = [];
  let hourBits = [];
  let providers = [];
  let attempts = [];
  let attemptItems = [];
  let contributionSummaries = [];
  let ledgerAggregate = null;
  let paymentAttempt = null;
  let publicQuotePolicy = null;
  let operationsSummary = null;
  let riskObservations = [];
  let currentUser = null;
  let operatorAccount = null;
  let operatorMembership = null;
  let projects = [];
  let selectedProjectId = '';
  let settingsProjectId = '';
  let settingsProject = null;
  let selectedInvoiceId = '';
  let pendingSelectedInvoiceId = '';
  // Presentational only: which of the mobile-collapsed operator-grid panels
  // (list vs. detail) is showing. Never read for data-fetching decisions.
  let invoiceDetailOpenOnMobile = false;
  let invoicePage = 1;
  let showArchived = false;
  let projectNameSeededForProject = '';
  let policySeededForProject = '';
  let projectName = '';
  let savingProjectName = false;
  let savingProjectPolicy = false;
  let newProjectName = '';
  let newProjectCurrency = DEFAULT_OPERATIONAL_CURRENCY;
  let newProjectShowPaymentProgressPercent = true;
  let creatingProject = false;
  let projectCurrency = 'CAD';
  let settlementCadence = 'immediate';
  let processorFeeQuotePolicyId = 'stripe-ca-domestic-card-2026-08';
  let transferTax = true;
  let showPaymentProgressPercent = true;
  let payerFeeShareBps = 5000;
  let operatorFeeShareBps = 5000;
  let selectedThemeId = DEFAULT_THEME_ID;
  let themeSavePending = false;
  let pendingThemeId = null;
  let accountCurrencySeededFor = '';
  let accountDefaultCurrency = DEFAULT_OPERATIONAL_CURRENCY;
  let accountCurrencySavePending = false;
  let pendingCurrency = null;
  let newInvoiceCurrency = DEFAULT_OPERATIONAL_CURRENCY;
  let creatingInvoice = false;
  let savingInvoiceDraft = false;
  let invoiceDraftSeededFor = '';
  let draftTitle = '';
  let draftDueDate = '';
  let draftTaxPercent = '0';
  let draftMinimumRate = '1';
  let draftCurrency = DEFAULT_OPERATIONAL_CURRENCY;
  let editingHourBitId = '';
  let bitLabel = '';
  let bitDescription = '';
  let bitHours = '';
  let bitFixedCost = '';
  let bitReported = true;
  let savingHourBit = false;
  let selectedBitIds = [];
  let providerId = 'stripe';
  let hourlyRate = '';
  let flatAddition = '';
  let flatAdditionReason = '';
  let payerEmail = '';
  let payerName = '';
  let payerCompany = '';
  let loginEmail = '';
  let loginPassword = '';
  let creatingIntent = false;
  let startingNextStep = false;
  let retryingCheckout = false;
  let returnReconciliationStartedFor = '';
  let selectionSeededForInvoice = '';
  let subscriptionFailed = false;
  let instanceMode = INSTANCE_MODES.MULTI;
  let instanceConfigDoc = null;
  let payerLinkInput = '';
  let configHandle;
  let configReady = false;
  let configComputation;

  const identityTracker = createOverlappingSubscription((...args) => Meteor.subscribe(...args));
  const operatorInvoicesTracker = createOverlappingSubscription((...args) => Meteor.subscribe(...args));
  const operatorInvoiceDetailTracker = createOverlappingSubscription((...args) => Meteor.subscribe(...args));
  const payerInvoiceTracker = createOverlappingSubscription((...args) => Meteor.subscribe(...args));
  const paymentAttemptTracker = createOverlappingSubscription((...args) => Meteor.subscribe(...args));
  const homeIndexTracker = createOverlappingSubscription((...args) => Meteor.subscribe(...args));
  const openInvoiceDetailTracker = createOverlappingSubscription((...args) => Meteor.subscribe(...args));

  let sessionComputation;
  let identityComputation;
  let operatorInvoicesComputation;
  let operatorInvoiceDetailComputation;
  let payerInvoiceComputation;
  let paymentAttemptComputation;
  let homeIndexComputation;
  let openInvoiceDetailComputation;

  let identityStartedForUserId; // undefined = not started
  let operatorInvoicesStartedKey;
  let operatorInvoiceDetailStartedForId;
  let payerInvoiceStartedForToken;
  let paymentAttemptStartedForToken;
  let homeIndexStartedForPage;
  let openInvoiceDetailStartedForToken;

  let identityReady = true;
  let operatorInvoicesReady = true;
  let operatorInvoiceDetailReady = true;
  let payerInvoiceReady = false;
  let paymentAttemptReady = false;
  let homeIndexReady = false;
  let openInvoiceDetailReady = true;

  $: route = routeFromPath(path);
  $: currentUserId = currentUser?._id || null;
  $: checkoutReturnHint = route.name === 'pay'
    ? new URLSearchParams(window.location.search).get('checkout')
    : '';
  $: ready =
    route.name === 'operator'
      ? configReady && identityReady &&
        (!currentUserId || route.section !== 'desk' || projects.length === 0 ||
          (Boolean(selectedProjectId) && operatorInvoicesReady)) :
    route.name === 'invoice' ? configReady && payerInvoiceReady :
    route.name === 'pay' ? configReady && paymentAttemptReady :
    route.name === 'home' ? configReady && homeIndexReady :
    // Every remaining route still waits for app.publicConfig: instance mode and
    // branding decide what the shell around it says.
    configReady;
  $: isOperator = Boolean(currentUser && operatorMembership);
  $: canManageOperatorAccount = ['owner', 'admin'].includes(operatorMembership?.role);
  $: routeKey = routeKeyFor(route);
  // One place decides which of the four states a route is in (see
  // imports/ui/route-states.mjs). Every field here is a fact this component
  // already holds; nothing is fetched to answer the question.
  $: routeState = resolveRouteState(routeKey, {
    ready,
    subscriptionFailed,
    pathUnmatched: Boolean(route.pathUnmatched),
    soloEntry: instanceMode === INSTANCE_MODES.SOLO,
    signedIn: Boolean(currentUserId),
    isOperator,
    canManage: canManageOperatorAccount,
    hasProject: routeKey === 'operatorProjectSettings'
      ? Boolean(settingsProject)
      : Boolean(selectedProject),
    invoiceCount: visibleInvoices.length,
    page: invoicePage,
    sectionCount: INTRO_SECTIONS.length,
    invoice,
    reportedWorkCount: reportedBits.length,
    paymentAttempt,
    receiptItemCount: attemptItems.length,
  });
  // loading, denied and not-found take over the route; empty renders in place,
  // inside the route's own layout, because an empty route still needs its own
  // controls (route-states.mjs explains why).
  $: routeStateTakesOver = [
    ROUTE_STATES.LOADING,
    ROUTE_STATES.DENIED,
    ROUTE_STATES.NOT_FOUND,
  ].includes(routeState);
  $: skeletonVariant =
    routeKey === 'invoice' ? 'payer' :
    routeKey === 'pay' ? 'payment' :
    routeKey === 'operator' ||
    routeKey === 'operatorAccountSettings' ||
    routeKey === 'operatorNewProject' ||
    routeKey === 'operatorProjectSettings' ? 'operator' :
    'page';
  $: loadingMessage =
    routeKey === 'operator' ? 'Loading operator desk' :
    routeKey === 'operatorAccountSettings' ? 'Loading account settings' :
    routeKey === 'operatorNewProject' ? 'Loading new project' :
    routeKey === 'operatorProjectSettings' ? 'Loading project settings' :
    routeKey === 'invoice' ? 'Loading this page' :
    routeKey === 'pay' ? 'Loading your payment' :
    routeKey === 'intro' ? 'Loading how it works' :
    'Loading open pages';
  // A stranger who opened a private link to pay someone gets the record and
  // nothing else: no marketing hero, no site nav pointing at operator sign-in,
  // no construction banner above their card details.
  $: isPayerRoute = ['invoice', 'pay'].includes(route.name);
  $: showSiteChrome = !isPayerRoute && route.name !== 'operator';
  $: isBrandedRoute =
    ['invoice', 'pay', 'operator'].includes(route.name) ||
    (['home', 'intro'].includes(route.name) && instanceMode === INSTANCE_MODES.SOLO);
  // The operator route reads the authenticated OperatorAccountsCollection doc
  // (operatorAccount, from operator.identity). Every other branded route reads the
  // operatorBranding field embedded on that route's own publicly-fetched doc
  // (invoice/paymentAttempt/instanceConfigDoc) instead — see server/publications.js.
  // In multi mode isBrandedRoute is false for home/intro, so instanceConfigDoc's
  // (solo-only) operatorBranding simply goes unused there.
  $: routeOperatorBranding =
    route.name === 'invoice' ? invoice?.operatorBranding :
    route.name === 'pay' ? paymentAttempt?.operatorBranding :
    (route.name === 'home' || route.name === 'intro') ? instanceConfigDoc?.operatorBranding :
    null;
  $: activeThemeId = isBrandedRoute
    ? normalizeThemeId(route.name === 'operator' ? selectedThemeId : routeOperatorBranding?.themeId)
    : DEFAULT_THEME_ID;
  // U07, the name rollout: the product calls itself Meritmark and nothing else.
  // It replaced "Invoice signal" beside an operator's own name and "Meteor invoice
  // machine" standing alone. The public noun for the document is a separate open
  // decision, so "invoice" is untouched everywhere it names the thing rather than
  // the product.
  $: brandEyebrow =
    (route.name === 'operator' ? operatorAccount?.name : routeOperatorBranding?.name)
      ? `${route.name === 'operator' ? operatorAccount.name : routeOperatorBranding.name} · Meritmark`
      : 'Meritmark';
  $: document.documentElement.dataset.theme = activeThemeId;
  $: selectedProject = projects.find((project) => project._id === selectedProjectId) || null;
  $: settingsProjectId = route.section === 'projectSettings' ? route.projectId || '' : '';
  $: settingsProject = projects.find((project) => project._id === settingsProjectId) || null;
  $: if (settingsProject && projectNameSeededForProject !== settingsProject._id) {
    projectName = settingsProject.name;
    projectNameSeededForProject = settingsProject._id;
  }
  $: if (settingsProject && policySeededForProject !== settingsProject._id) {
    projectCurrency = settingsProject.currency;
    settlementCadence = settingsProject.settlementCadence;
    processorFeeQuotePolicyId = settingsProject.processorFeeQuotePolicyId;
    transferTax = settingsProject.transferTax;
    showPaymentProgressPercent = settingsProject.showPaymentProgressPercent ?? true;
    payerFeeShareBps = settingsProject.payerProcessorFeeShareBps;
    operatorFeeShareBps = settingsProject.operatorProcessorFeeShareBps;
    policySeededForProject = settingsProject._id;
  }
  $: availableProviders = providers.filter(
    (provider) =>
      provider.status === 'available' ||
      (provider.providerId === 'manual' && invoice?.manual?.magicUrl),
  );
  $: if (
    availableProviders.length > 0 &&
    !availableProviders.some((provider) => provider.providerId === providerId)
  ) {
    providerId = availableProviders[0].providerId;
  }
  $: selectedProvider = providers.find((provider) => provider.providerId === providerId) || null;
  $: selectedFeeQuotePolicy =
    selectedProvider?.feeQuotePolicies?.find(
      (policy) => policy.policyId === publicQuotePolicy?.processorFeeQuotePolicyId,
    ) ||
    ZERO_FEE_QUOTE_POLICY;
  $: payerFeeQuoteAvailable =
    providerId !== 'stripe' || selectedFeeQuotePolicy.policyId !== ZERO_FEE_QUOTE_POLICY.policyId;
  $: visibleInvoices = invoices.slice(0, ACTIVE_INVOICE_PAGE_SIZE);
  $: hasNextInvoicePage = invoices.length > ACTIVE_INVOICE_PAGE_SIZE;
  $: hasNoInvoicesHere = visibleInvoices.length === 0;
  $: canCreateInvoice = Boolean(selectedProject);
  $: reportedBits = hourBits.filter((bit) => bit.reported);
  $: selectedBits = reportedBits.filter((bit) => selectedBitIds.includes(bit._id));
  $: selectedHours = selectedBits.reduce((sum, bit) => sum + bit.hours, 0);
  $: selectedFixedCostsMinor = selectedBits.reduce(
    (sum, bit) => sum + (bit.fixedCostMinor || 0),
    0,
  );
  $: quote = calculatePaymentQuote({
    hours: selectedHours,
    hourlyRateMinor: dollarsToMinor(hourlyRate),
    fixedCostsMinor: selectedFixedCostsMinor,
    flatAdditionMinor: dollarsToMinor(flatAddition),
    taxRateBps: invoice?.taxRateBps || 0,
    feeQuotePolicy: selectedFeeQuotePolicy,
    payerProcessorFeeShareBps:
      providerId === 'stripe' ? publicQuotePolicy?.payerProcessorFeeShareBps || 0 : 0,
  });
  $: canCreateIntent =
    invoice &&
    isInvoicePayable(invoice) &&
    selectedBits.length > 0 &&
    dollarsToMinor(hourlyRate) >= 100 &&
    payerEmail.includes('@') &&
    availableProviders.some((provider) => provider.providerId === providerId) &&
    !creatingIntent;
  // Same conditions as canCreateIntent, but phrased for the payer. The submit
  // button stays enabled and reports these on press; a disabled control gives no
  // feedback on touch and never explains itself.
  $: payerBlockers = !invoice || !isInvoicePayable(invoice)
    ? []
    : [
        ...(selectedBits.length === 0
          ? ['Choose at least one piece of work to pay for.']
          : []),
        // The asking rate is a watermark, not a floor (decided 2026-08-08/09):
        // paying under it is allowed, so the only rate blocker left is the
        // server's own CA$1.00 sanity bound, mirrored here.
        ...(dollarsToMinor(hourlyRate) < 100
          ? [
              `Enter an hourly rate of at least ${minorToMoney(
                100,
                invoice.currency,
              )}.`,
            ]
          : []),
        ...(!payerEmail.includes('@')
          ? ['Enter an email address so your receipt has somewhere to go.']
          : []),
        ...(!availableProviders.some((provider) => provider.providerId === providerId)
          ? ['No payment method is available right now. Try again shortly.']
          : []),
      ];
  $: suggestedHours = reportedBits.reduce((sum, bit) => sum + bit.hours, 0);
  $: suggestedFixedCostsMinor = reportedBits.reduce(
    (sum, bit) => sum + (bit.fixedCostMinor || 0),
    0,
  );
  $: suggestedQuote = calculatePaymentQuote({
    hours: suggestedHours,
    hourlyRateMinor: invoice?.minHourlyRateMinor || 0,
    fixedCostsMinor: suggestedFixedCostsMinor,
    flatAdditionMinor: 0,
    taxRateBps: invoice?.taxRateBps || 0,
    feeQuotePolicy: selectedFeeQuotePolicy,
    payerProcessorFeeShareBps:
      providerId === 'stripe' ? publicQuotePolicy?.payerProcessorFeeShareBps || 0 : 0,
  });
  $: payerQuoteDiffersFromSuggestion =
    quote.hours !== suggestedQuote.hours ||
    quote.baseMinor !== suggestedQuote.baseMinor ||
    quote.fixedCostsMinor !== suggestedQuote.fixedCostsMinor ||
    quote.flatAdditionMinor !== 0 ||
    quote.chargeTotalMinor !== suggestedQuote.chargeTotalMinor;
  $: operatorFeeQuotePolicy = feeQuotePolicyForInvoice(
    selectedProject?.processorFeeQuotePolicyId,
    invoice?.currency,
  );
  $: operatorFeeQuoteAvailable =
    operatorFeeQuotePolicy.policyId !== ZERO_FEE_QUOTE_POLICY.policyId;
  $: operatorExpectedQuote = calculatePaymentQuote({
    hours: reportedBits.reduce((sum, bit) => sum + bit.hours, 0),
    hourlyRateMinor: invoice?.minHourlyRateMinor || 0,
    fixedCostsMinor: reportedBits.reduce(
      (sum, bit) => sum + (bit.fixedCostMinor || 0),
      0,
    ),
    flatAdditionMinor: 0,
    taxRateBps: invoice?.taxRateBps || 0,
    feeQuotePolicy: operatorFeeQuotePolicy || ZERO_FEE_QUOTE_POLICY,
    payerProcessorFeeShareBps: operatorFeeQuoteAvailable
      ? selectedProject?.payerProcessorFeeShareBps || 0
      : 0,
  });
  $: invoiceDraftDirty = invoice?.status === 'draft' && (
    draftTitle.trim() !== String(invoice.recipientLabel || '') ||
    draftDueDate !== dateInputValue(invoice.dueAt) ||
    Math.round(Number(draftTaxPercent) * 100) !== invoice.taxRateBps ||
    dollarsToMinor(draftMinimumRate) !== invoice.minHourlyRateMinor ||
    draftCurrency !== invoice.currency
  );
  $: draftTaxRateBps = Math.round(Number(draftTaxPercent) * 100);
  $: draftMinimumRateMinor = dollarsToMinor(draftMinimumRate);
  $: invoiceDraftFormValid =
    draftTitle.trim().length > 0 &&
    draftTitle.trim().length <= 120 &&
    !Number.isNaN(new Date(draftDueDate).getTime()) &&
    Number.isSafeInteger(draftTaxRateBps) &&
    draftTaxRateBps >= 0 &&
    draftTaxRateBps <= 10_000 &&
    Number.isSafeInteger(draftMinimumRateMinor) &&
    draftMinimumRateMinor >= 100 &&
    draftMinimumRateMinor <= 100_000_000;
  $: invoiceIssuanceIssues = invoice?.status === 'draft'
    ? [
        ...(!invoiceDraftFormValid ? ['Correct and save the invoice details before issuing.'] : []),
        ...(invoiceDraftDirty ? ['Save invoice-detail changes before issuing.'] : []),
        ...(reportedBits.length === 0 ? ['Add at least one reported payable item.'] : []),
        ...((invoice.minHourlyRateMinor || 0) < 100 ? ['Set a valid invoice hourly rate.'] : []),
        ...(selectedProject?.settlementCadence !== 'immediate'
          ? ['Immediate payment cadence is required for Checkout.']
          : []),
      ]
    : [];
  $: invoiceReadinessWarnings = invoice?.status === 'draft' && !providers.some(
    (provider) => provider.providerId === 'stripe' && provider.status === 'available',
  )
    ? ['Stripe Checkout is not currently available to payers. The invoice can still be issued.']
    : [];

  // `pathUnmatched` is how a route says "this address is not mine". Extra
  // segments used to be swallowed silently — /intro/anything and
  // /operator/anything rendered their base route, /invoices with no token
  // rendered the home index, and every unknown path in the application rendered
  // home. None of those addresses exist, so each now resolves to its route's
  // not-found state instead of quietly showing something else.
  function routeFromPath(nextPath) {
    const parts = nextPath.split('/').filter(Boolean);

    if (parts[0] === 'invoices') {
      return { name: 'invoice', token: parts[1] || '', pathUnmatched: parts.length !== 2 };
    }

    if (parts[0] === 'pay') {
      return { name: 'pay', token: parts[1] || '', pathUnmatched: parts.length !== 2 };
    }

    if (parts[0] === 'intro') {
      return { name: 'intro', pathUnmatched: parts.length > 1 };
    }

    if (parts[0] === 'operator') {
      if (parts[1] === 'settings' || parts[1] === 'account-settings') {
        return {
          name: 'operator',
          section: 'accountSettings',
          pathUnmatched: parts.length !== 2,
        };
      }

      if (parts[1] === 'projects') {
        if (parts[2] === 'new') {
          return {
            name: 'operator',
            section: 'newProject',
            pathUnmatched: parts.length !== 3,
          };
        }

        return {
          name: 'operator',
          section: 'projectSettings',
          projectId: parts[2] || '',
          pathUnmatched: parts.length !== 4 || parts[3] !== 'settings',
        };
      }

      return {
        name: 'operator',
        section: 'desk',
        pathUnmatched: parts.length > 1,
      };
    }

    return { name: 'home', pathUnmatched: parts.length > 0 };
  }

  function navigate(nextPath) {
    window.history.pushState({}, '', nextPath);
    path = window.location.pathname;
  }

  function startSession() {
    sessionComputation = Tracker.autorun(() => {
      currentUser = Meteor.user();
      instanceConfigDoc = RuntimeConfigCollection.findOne(INSTANCE_CONFIG_ID) || null;
      instanceMode = instanceConfigDoc?.mode || INSTANCE_MODES.MULTI;
    });
  }

  function stopSession() {
    sessionComputation?.stop?.();
    sessionComputation = null;
  }

  // Each concern below owns exactly one subscription + one Tracker.autorun, started/stopped
  // only when its own relevant inputs change (see the reactive statements after this block).
  // None of them share a computation with any other concern, so an unrelated concern's data
  // arriving (e.g. a webhook updating payment risk) can never cause another concern's derived
  // state (e.g. the theme selector) to be reevaluated.

  function ensureOperatorIdentity(userId) {
    if (identityStartedForUserId === userId) return;
    identityStartedForUserId = userId;
    identityComputation?.stop?.();
    identityReady = false;
    subscriptionFailed = false;
    const descriptor = operatorIdentitySubscription(userId);
    const subHandle = identityTracker.subscribe(descriptor.name, descriptor.args, {
      onError(error) {
        subscriptionFailed = true;
        identityReady = true;
        errorMessage = error.reason || error.message;
      },
    });
    identityComputation = Tracker.autorun(() => {
      identityReady = subscriptionFailed || Boolean(subHandle?.ready?.());
      // OperatorAccountsCollection is also where the always-on app.publicConfig
      // subscription (solo mode) delivers a redacted, synthetically-keyed branding
      // doc for the payer entry screen — same client collection, different
      // document. A selector-less findOne() can pick that one instead of the real
      // authenticated account, since both are present in Minimongo simultaneously.
      // OperatorMembershipsCollection is never touched by any other publication
      // (OPERATOR_MEMBERSHIP_FIELDS also excludes userId, so it can't be filtered
      // on client-side anyway), so a bare findOne() there is safe; its
      // operatorAccountId is then used to pin down the one real account doc.
      operatorMembership = OperatorMembershipsCollection.findOne() || null;
      operatorAccount = operatorMembership
        ? OperatorAccountsCollection.findOne(operatorMembership.operatorAccountId) || null
        : null;
      projects = ProjectsCollection.find({}, { sort: { name: 1 } }).fetch();
      providers = ProviderConfigsCollection.find({}, { sort: { providerId: 1 } }).fetch();
      operationsSummary = PaymentOperationsSummariesCollection.findOne() || null;

      if (operatorAccount) {
        const serverThemeId = normalizeThemeId(operatorAccount.themeId);
        if (pendingThemeId && serverThemeId === pendingThemeId) pendingThemeId = null;
        if (!pendingThemeId) selectedThemeId = serverThemeId;

        const serverDefaultCurrency = normalizeOperationalCurrency(operatorAccount.defaultCurrency);
        if (pendingCurrency && serverDefaultCurrency === pendingCurrency) pendingCurrency = null;
        if (!pendingCurrency) accountDefaultCurrency = serverDefaultCurrency;

        if (accountCurrencySeededFor !== operatorAccount._id) {
          newInvoiceCurrency = serverDefaultCurrency;
          newProjectCurrency = serverDefaultCurrency;
          accountCurrencySeededFor = operatorAccount._id;
        }
      }

      if (!selectedProjectId && projects[0]) {
        selectedProjectId = projects[0]._id;
      }
    });
  }

  function teardownOperatorIdentity() {
    identityReady = true;
    if (identityStartedForUserId === undefined) return;
    identityComputation?.stop?.();
    identityComputation = null;
    identityTracker.stop();
    identityStartedForUserId = undefined;
    operatorAccount = null;
    operatorMembership = null;
    projects = [];
    operationsSummary = null;
  }

  function ensureOperatorInvoices(projectId, page, archived) {
    const key = `${projectId}:${page}:${archived}`;
    if (operatorInvoicesStartedKey === key) return;
    operatorInvoicesStartedKey = key;
    operatorInvoicesComputation?.stop?.();
    operatorInvoicesReady = false;
    subscriptionFailed = false;
    const descriptor = operatorInvoicesSubscription({ projectId, page, archived });
    const subHandle = operatorInvoicesTracker.subscribe(descriptor.name, descriptor.args, {
      onError(error) {
        subscriptionFailed = true;
        operatorInvoicesReady = true;
        errorMessage = error.reason || error.message;
      },
    });
    operatorInvoicesComputation = Tracker.autorun(() => {
      operatorInvoicesReady = subscriptionFailed || Boolean(subHandle?.ready?.());
      invoices = InvoicesCollection.find({}, { sort: { updatedAt: -1, _id: -1 } }).fetch();
      const selectedInvoice = invoices.find(
        (candidate) => candidate._id === selectedInvoiceId,
      );
      if (selectedInvoice) {
        invoice = selectedInvoice;
        if (pendingSelectedInvoiceId === selectedInvoice._id) pendingSelectedInvoiceId = '';
      } else if (selectedInvoiceId && selectedInvoiceId === pendingSelectedInvoiceId) {
        invoice = null;
      } else {
        invoice = invoices[0] || null;
        selectedInvoiceId = invoice?._id || '';
        pendingSelectedInvoiceId = '';
      }
      if (invoice?.status === 'draft' && invoiceDraftSeededFor !== invoice._id) {
        seedInvoiceDraft(invoice);
      }
    });
  }

  function teardownOperatorInvoices() {
    operatorInvoicesReady = true;
    if (operatorInvoicesStartedKey === undefined) return;
    operatorInvoicesComputation?.stop?.();
    operatorInvoicesComputation = null;
    operatorInvoicesTracker.stop();
    operatorInvoicesStartedKey = undefined;
    invoices = [];
    invoice = null;
    selectedInvoiceId = '';
    pendingSelectedInvoiceId = '';
  }

  function ensureOperatorInvoiceDetail(invoiceId) {
    if (operatorInvoiceDetailStartedForId === invoiceId) return;
    operatorInvoiceDetailStartedForId = invoiceId;
    operatorInvoiceDetailComputation?.stop?.();
    operatorInvoiceDetailReady = false;
    const descriptor = operatorInvoiceDetailSubscription(invoiceId);
    const subHandle = operatorInvoiceDetailTracker.subscribe(descriptor.name, descriptor.args, {
      onError(error) {
        operatorInvoiceDetailReady = true;
        errorMessage = error.reason || error.message;
      },
    });
    operatorInvoiceDetailComputation = Tracker.autorun(() => {
      operatorInvoiceDetailReady = Boolean(subHandle?.ready?.());
      hourBits = HourBitsCollection.find(
        { invoiceId },
        { sort: { sortOrder: 1 } },
      ).fetch();
      attempts = PaymentAttemptsCollection.find(
        { invoiceId },
        { sort: { createdAt: -1 } },
      ).fetch();
      attemptItems = PaymentAttemptItemsCollection.find({ invoiceId }).fetch();
      riskObservations = PaymentRiskObservationsCollection.find(
        {},
        { sort: { blocking: -1, updatedAt: -1 } },
      ).fetch();
    });
  }

  function teardownOperatorInvoiceDetail() {
    operatorInvoiceDetailReady = true;
    if (operatorInvoiceDetailStartedForId === undefined) return;
    operatorInvoiceDetailComputation?.stop?.();
    operatorInvoiceDetailComputation = null;
    operatorInvoiceDetailTracker.stop();
    operatorInvoiceDetailStartedForId = undefined;
    hourBits = [];
    attempts = [];
    attemptItems = [];
    riskObservations = [];
  }

  function ensurePayerInvoice(token) {
    if (payerInvoiceStartedForToken === token) return;
    payerInvoiceStartedForToken = token;
    payerInvoiceComputation?.stop?.();
    payerInvoiceReady = false;
    subscriptionFailed = false;
    const descriptor = subscriptionForRoute({ name: 'invoice', token }, null);
    const subHandle = payerInvoiceTracker.subscribe(descriptor.name, descriptor.args, {
      onError(error) {
        subscriptionFailed = true;
        payerInvoiceReady = true;
        errorMessage = error.reason || error.message;
      },
    });
    payerInvoiceComputation = Tracker.autorun(() => {
      payerInvoiceReady = subscriptionFailed || Boolean(subHandle?.ready?.());
      invoice = InvoicesCollection.findOne(token) || null;
      hourBits = invoice
        ? HourBitsCollection.find({}, { sort: { sortOrder: 1 } }).fetch()
        : [];
      providers = ProviderConfigsCollection.find({}, { sort: { providerId: 1 } }).fetch();
      contributionSummaries = InvoiceContributionSummariesCollection.find().fetch();
      ledgerAggregate = InvoiceLedgerAggregatesCollection.findOne() || null;
      publicQuotePolicy = PaymentQuotePoliciesCollection.findOne() || null;

      if (invoice && selectionSeededForInvoice !== invoice._id) {
        selectedBitIds = hourBits.filter((bit) => bit.reported).map((bit) => bit._id);
        // Seeded with the asking rate (restored 2026-08-08, Kai; U01 shipped it
        // empty). The ask is a watermark, not a floor -- the payer edits freely
        // in either direction, and the form says so beside the field.
        hourlyRate = String((invoice.minHourlyRateMinor || 100) / 100);
        flatAddition = '';
        flatAdditionReason = '';
        selectionSeededForInvoice = invoice._id;
      }
    });
  }

  function teardownPayerInvoice() {
    payerInvoiceReady = false;
    if (payerInvoiceStartedForToken === undefined) return;
    payerInvoiceComputation?.stop?.();
    payerInvoiceComputation = null;
    payerInvoiceTracker.stop();
    payerInvoiceStartedForToken = undefined;
    invoice = null;
    hourBits = [];
    contributionSummaries = [];
    ledgerAggregate = null;
    publicQuotePolicy = null;
    selectionSeededForInvoice = '';
  }

  function ensurePaymentAttempt(token) {
    if (paymentAttemptStartedForToken === token) return;
    paymentAttemptStartedForToken = token;
    paymentAttemptComputation?.stop?.();
    paymentAttemptReady = false;
    subscriptionFailed = false;
    const descriptor = subscriptionForRoute({ name: 'pay', token }, null);
    const subHandle = paymentAttemptTracker.subscribe(descriptor.name, descriptor.args, {
      onError(error) {
        subscriptionFailed = true;
        paymentAttemptReady = true;
        errorMessage = error.reason || error.message;
      },
    });
    paymentAttemptComputation = Tracker.autorun(() => {
      paymentAttemptReady = subscriptionFailed || Boolean(subHandle?.ready?.());
      paymentAttempt = PaymentAttemptsCollection.findOne() || null;
      attemptItems = PaymentAttemptItemsCollection.find().fetch();
    });
  }

  function teardownPaymentAttempt() {
    paymentAttemptReady = false;
    if (paymentAttemptStartedForToken === undefined) return;
    paymentAttemptComputation?.stop?.();
    paymentAttemptComputation = null;
    paymentAttemptTracker.stop();
    paymentAttemptStartedForToken = undefined;
    paymentAttempt = null;
    attemptItems = [];
  }

  function ensureHomeIndex(page) {
    if (homeIndexStartedForPage === page) return;
    homeIndexStartedForPage = page;
    homeIndexComputation?.stop?.();
    homeIndexReady = false;
    subscriptionFailed = false;
    const descriptor = subscriptionForRoute({ name: 'home' }, null, { page });
    const subHandle = homeIndexTracker.subscribe(descriptor.name, descriptor.args, {
      onError(error) {
        subscriptionFailed = true;
        homeIndexReady = true;
        errorMessage = error.reason || error.message;
      },
    });
    homeIndexComputation = Tracker.autorun(() => {
      homeIndexReady = subscriptionFailed || Boolean(subHandle?.ready?.());
      invoices = InvoicesCollection.find({}, { sort: { issuedAt: -1, _id: -1 } }).fetch();
      invoice =
        invoices.find((candidate) => candidate._id === selectedInvoiceId) ||
        invoices[0] ||
        null;
      if (invoice) selectedInvoiceId = invoice._id;
    });
  }

  function teardownHomeIndex() {
    homeIndexReady = false;
    if (homeIndexStartedForPage === undefined) return;
    homeIndexComputation?.stop?.();
    homeIndexComputation = null;
    homeIndexTracker.stop();
    homeIndexStartedForPage = undefined;
    invoices = [];
    invoice = null;
    selectedInvoiceId = '';
  }

  function ensureOpenInvoiceDetail(codenameToken) {
    if (openInvoiceDetailStartedForToken === codenameToken) return;
    openInvoiceDetailStartedForToken = codenameToken;
    openInvoiceDetailComputation?.stop?.();
    openInvoiceDetailReady = false;
    const descriptor = openInvoiceDetailSubscription(codenameToken);
    const subHandle = openInvoiceDetailTracker.subscribe(descriptor.name, descriptor.args, {
      onError(error) {
        openInvoiceDetailReady = true;
        errorMessage = error.reason || error.message;
      },
    });
    openInvoiceDetailComputation = Tracker.autorun(() => {
      openInvoiceDetailReady = Boolean(subHandle?.ready?.());
      hourBits = HourBitsCollection.find(
        { invoiceToken: codenameToken },
        { sort: { sortOrder: 1 } },
      ).fetch();
    });
  }

  function teardownOpenInvoiceDetail() {
    openInvoiceDetailReady = true;
    if (openInvoiceDetailStartedForToken === undefined) return;
    openInvoiceDetailComputation?.stop?.();
    openInvoiceDetailComputation = null;
    openInvoiceDetailTracker.stop();
    openInvoiceDetailStartedForToken = undefined;
    hourBits = [];
  }

  onMount(() => {
    // Every route depends on this one, so its failure is what puts any route
    // into the denied state rather than leaving it loading forever.
    configHandle = Meteor.subscribe('app.publicConfig', {
      onError(error) {
        subscriptionFailed = true;
        // The autorun below recomputes configReady from the handle, which stays
        // unready forever after an error. Stop it first, or it overwrites this
        // and every route waits on a skeleton that will never resolve.
        configComputation?.stop?.();
        configComputation = null;
        configReady = true;
        errorMessage = error.reason || error.message;
      },
    });
    configComputation = Tracker.autorun(() => {
      configReady = Boolean(configHandle?.ready?.());
    });
    startSession();
    const onPopState = () => {
      path = window.location.pathname;
    };
    window.addEventListener('popstate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
      stopSession();
      teardownOperatorIdentity();
      teardownOperatorInvoices();
      teardownOperatorInvoiceDetail();
      teardownPayerInvoice();
      teardownPaymentAttempt();
      teardownHomeIndex();
      teardownOpenInvoiceDetail();
    };
  });

  onDestroy(() => {
    stopSession();
    teardownOperatorIdentity();
    teardownOperatorInvoices();
    teardownOperatorInvoiceDetail();
    teardownPayerInvoice();
    teardownPaymentAttempt();
    teardownHomeIndex();
    teardownOpenInvoiceDetail();
    configComputation?.stop?.();
    configHandle?.stop?.();
    delete document.documentElement.dataset.theme;
  });

  // Each concern starts or stops independently, only when its own inputs actually change —
  // switching invoice pages, for instance, only touches ensureOperatorInvoices below, never
  // operator identity/theme state.
  $: if (route.name === 'operator' && currentUserId) {
    ensureOperatorIdentity(currentUserId);
  } else {
    teardownOperatorIdentity();
  }

  $: if (route.name === 'operator' && route.section === 'desk' && currentUserId && selectedProjectId) {
    ensureOperatorInvoices(selectedProjectId, invoicePage, showArchived);
  } else if (!(route.name === 'operator' && route.section === 'desk' && currentUserId)) {
    teardownOperatorInvoices();
  }

  $: if (route.name === 'operator' && route.section === 'desk' && currentUserId && invoice?._id) {
    ensureOperatorInvoiceDetail(invoice._id);
  } else {
    teardownOperatorInvoiceDetail();
  }

  $: if (route.name === 'invoice') {
    ensurePayerInvoice(route.token);
  } else {
    teardownPayerInvoice();
  }

  $: if (route.name === 'pay') {
    ensurePaymentAttempt(route.token);
  } else {
    teardownPaymentAttempt();
  }

  $: if (route.name === 'home') {
    ensureHomeIndex(invoicePage);
  } else {
    teardownHomeIndex();
  }

  $: if (route.name === 'home' && instanceMode === INSTANCE_MODES.MULTI && invoice?._id) {
    ensureOpenInvoiceDetail(invoice._id);
  } else {
    teardownOpenInvoiceDetail();
  }

  function toggleBit(bitId) {
    selectedBitIds = selectedBitIds.includes(bitId)
      ? selectedBitIds.filter((id) => id !== bitId)
      : [...selectedBitIds, bitId];
  }

  function openPayerInvoice() {
    errorMessage = '';
    noticeMessage = '';
    try {
      const token = parsePayerInvoiceReference(payerLinkInput, window.location.origin);
      navigate(`/invoices/${token}`);
    } catch (error) {
      errorMessage = error.message;
    }
  }

  function bitRates(bit) {
    // Bearer route: the publication carries a boolean and whole-number progress
    // percentage per bit, never the underlying amount. The fallback below is the
    // operator route, which still computes exact per-bit rate sums privately.
    const summary = contributionSummaries.find((candidate) => candidate._id === bit._id);
    if (summary) {
      return {
        hasContributions: !!summary.hasContributions,
        ...(Number.isFinite(summary.paymentPercent)
          ? { paymentPercent: summary.paymentPercent }
          : {}),
      };
    }

    return contributionRatesForBit(bit, attempts, attemptItems);
  }

  async function createIntent() {
    if (!canCreateIntent) return;
    creatingIntent = true;
    errorMessage = '';

    try {
      const result = await Meteor.callAsync('paymentAttempts.create', {
        invoiceToken: invoice.codenameToken || invoice._id,
        providerId,
        selectedHourBitPublicIds: selectedBits.map((bit) => bit._id),
        expectedQuote: {
          currency: invoice.currency,
          processorFeeQuotePolicyId: selectedFeeQuotePolicy.policyId,
          payerProcessorFeeShareBps: publicQuotePolicy?.payerProcessorFeeShareBps || 0,
          fixedCostsMinor: quote.fixedCostsMinor,
          flatAdditionMinor: quote.flatAdditionMinor,
          chargeTotalMinor: quote.chargeTotalMinor,
        },
        hourlyRateMinor: dollarsToMinor(hourlyRate),
        flatAdditionMinor: dollarsToMinor(flatAddition),
        flatAdditionReason,
        payerEmail,
        payerName,
        payerCompany,
      });
      navigate(`/pay/${result.attemptToken}`);
    } catch (error) {
      errorMessage = error.reason || error.message;
    } finally {
      creatingIntent = false;
    }
  }

  async function beginNextStep() {
    if (!paymentAttempt) return;
    startingNextStep = true;
    errorMessage = '';

    try {
      const result = await Meteor.callAsync('paymentAttempts.beginNextStep', route.token);
      noticeMessage = 'Payment intent saved. Continue when ready.';
      if (result.nextStepUrl) {
        window.location.href = result.nextStepUrl;
      }
    } catch (error) {
      errorMessage = error.reason || error.message;
    } finally {
      startingNextStep = false;
    }
  }

  async function reconcileCheckoutReturn() {
    if (!paymentAttempt || !['success', 'cancel'].includes(checkoutReturnHint)) return;
    const reconciliationKey = `${route.token}:${checkoutReturnHint}`;
    if (returnReconciliationStartedFor === reconciliationKey) return;
    returnReconciliationStartedFor = reconciliationKey;
    try {
      await Meteor.callAsync('paymentAttempts.reconcileReturn', route.token);
    } catch (error) {
      errorMessage = error.reason || error.message;
    }
  }

  async function retryCheckout() {
    if (!paymentAttempt || retryingCheckout) return;
    retryingCheckout = true;
    errorMessage = '';
    try {
      const result = await Meteor.callAsync('paymentAttempts.retryCheckout', route.token);
      if (result.nextStepUrl) window.location.href = result.nextStepUrl;
    } catch (error) {
      errorMessage = error.reason || error.message;
    } finally {
      retryingCheckout = false;
    }
  }

  $: if (route.name === 'pay' && paymentAttempt && checkoutReturnHint) {
    reconcileCheckoutReturn();
  }

  async function loginOperator() {
    errorMessage = '';

    try {
      await Meteor.loginWithPassword(loginEmail, loginPassword);
    } catch (error) {
      errorMessage = error.reason || error.message;
    }
  }

  async function logoutOperator() {
    errorMessage = '';

    try {
      await Meteor.logout();
    } catch (error) {
      errorMessage = error.reason || error.message;
    }
  }

  async function setReported(bit, reported) {
    try {
      await Meteor.callAsync('operator.hourBits.setReported', bit._id, reported);
    } catch (error) {
      errorMessage = error.reason || error.message;
    }
  }

  async function setInvoiceStatus(nextStatus) {
    if (!invoice) return;

    try {
      await Meteor.callAsync('operator.invoices.setStatus', invoice._id, nextStatus);
    } catch (error) {
      errorMessage = error.reason || error.message;
    }
  }

  function selectProject(projectId) {
    selectedProjectId = projectId;
    selectedInvoiceId = '';
    pendingSelectedInvoiceId = '';
    invoice = null;
    invoiceDetailOpenOnMobile = false;
    invoicePage = 1;
  }

  function returnToProjectDesk() {
    if (settingsProject) selectProject(settingsProject._id);
    navigate('/operator');
  }

  function selectProjectCurrency(event) {
    projectCurrency = String(event.target.value || '').toUpperCase();
    const matchingPolicy = STRIPE_FEE_QUOTE_POLICIES.find(
      (policy) => policy.currency === projectCurrency,
    );
    processorFeeQuotePolicyId =
      matchingPolicy?.policyId || UNCONFIGURED_STRIPE_FEE_QUOTE_POLICY_ID;
  }

  function selectInvoice(invoiceId) {
    const invoiceChanged = selectedInvoiceId !== invoiceId;
    selectedInvoiceId = invoiceId;
    pendingSelectedInvoiceId = '';
    invoiceDetailOpenOnMobile = true;
    invoice = invoices.find((candidate) => candidate._id === invoiceId) || null;
    // On mobile the first invoice is selected and subscribed while its detail
    // panel is hidden. Tapping that already-selected row only reveals the panel;
    // clearing its data here would leave it empty because the detail subscription
    // quite correctly has no reason to restart for the same invoice id.
    if (invoiceChanged) {
      hourBits = [];
      attempts = [];
      attemptItems = [];
      riskObservations = [];
    }
    invoiceDraftSeededFor = '';
    editingHourBitId = '';
    if (invoice?.status === 'draft') seedInvoiceDraft(invoice);
  }

  function changeInvoicePage(nextPage) {
    invoicePage = Math.max(1, nextPage);
    selectedInvoiceId = '';
    pendingSelectedInvoiceId = '';
    invoice = null;
    invoiceDetailOpenOnMobile = false;
  }

  function setArchiveView(archived) {
    showArchived = archived;
    invoicePage = 1;
    selectedInvoiceId = '';
    pendingSelectedInvoiceId = '';
    invoice = null;
    invoiceDetailOpenOnMobile = false;
  }

  async function setArchived(archived) {
    if (!invoice) return;
    try {
      await Meteor.callAsync('operator.invoices.setArchived', invoice._id, archived);
      invoiceDetailOpenOnMobile = false;
    } catch (error) {
      errorMessage = error.reason || error.message;
    }
  }

  async function createProject() {
    if (creatingProject) return;
    creatingProject = true;
    errorMessage = '';
    noticeMessage = '';
    try {
      const result = await Meteor.callAsync(
        'operator.projects.create',
        operatorMembership?.operatorAccountId,
        {
          name: newProjectName,
          currency: newProjectCurrency,
          showPaymentProgressPercent: newProjectShowPaymentProgressPercent,
        },
      );
      newProjectName = '';
      newProjectShowPaymentProgressPercent = true;
      selectProject(result.projectId);
      navigate(`/operator/projects/${result.projectId}/settings`);
      noticeMessage = 'Project created.';
    } catch (error) {
      errorMessage = error.reason || error.message;
    } finally {
      creatingProject = false;
    }
  }

  async function saveProjectName() {
    if (!settingsProject || savingProjectName) return;
    savingProjectName = true;
    errorMessage = '';
    noticeMessage = '';
    try {
      const result = await Meteor.callAsync('operator.projects.rename', settingsProject._id, projectName);
      projectName = result?.name || projectName;
      projectNameSeededForProject = settingsProject._id;
      noticeMessage = 'Project name saved.';
    } catch (error) {
      errorMessage = error.reason || error.message;
    } finally {
      savingProjectName = false;
    }
  }

  async function saveProjectPolicy() {
    if (!settingsProject || savingProjectPolicy) return;
    savingProjectPolicy = true;
    errorMessage = '';
    noticeMessage = '';
    try {
      await Meteor.callAsync('operator.projects.updatePolicy', settingsProject._id, {
        currency: projectCurrency,
        settlementCadence,
        processorFeeQuotePolicyId,
        transferTax,
        showPaymentProgressPercent,
        payerProcessorFeeShareBps: Number(payerFeeShareBps),
        operatorProcessorFeeShareBps: Number(operatorFeeShareBps),
      });
      noticeMessage = 'Project policy saved.';
    } catch (error) {
      errorMessage = error.reason || error.message;
    } finally {
      savingProjectPolicy = false;
    }
  }

  function dateInputValue(value) {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }

  function seedInvoiceDraft(draftInvoice) {
    draftTitle = draftInvoice.recipientLabel || '';
    draftDueDate = dateInputValue(draftInvoice.dueAt);
    draftTaxPercent = String((draftInvoice.taxRateBps || 0) / 100);
    draftMinimumRate = String((draftInvoice.minHourlyRateMinor || 100) / 100);
    draftCurrency = draftInvoice.currency;
    invoiceDraftSeededFor = draftInvoice._id;
  }

  async function createInvoice() {
    if (!selectedProject || creatingInvoice) return;
    creatingInvoice = true;
    errorMessage = '';
    noticeMessage = '';
    try {
      const result = await Meteor.callAsync(
        'operator.invoices.create',
        selectedProject._id,
        { currency: newInvoiceCurrency },
      );
      pendingSelectedInvoiceId = result.invoiceId;
      selectedInvoiceId = result.invoiceId;
      invoice = InvoicesCollection.findOne(result.invoiceId) || null;
      if (invoice) pendingSelectedInvoiceId = '';
      invoiceDetailOpenOnMobile = true;
      invoicePage = 1;
      showArchived = false;
      invoiceDraftSeededFor = '';
      noticeMessage = 'Draft invoice created.';
    } catch (error) {
      errorMessage = error.reason || error.message;
    } finally {
      creatingInvoice = false;
    }
  }

  async function saveInvoiceDraft() {
    if (!invoice || invoice.status !== 'draft' || savingInvoiceDraft) return;
    if (
      draftCurrency !== invoice.currency &&
      !window.confirm(
        `Change this draft from ${invoice.currency} to ${draftCurrency}? Existing amounts will be re-denominated, not converted.`,
      )
    ) {
      return;
    }
    savingInvoiceDraft = true;
    errorMessage = '';
    noticeMessage = '';
    try {
      await Meteor.callAsync('operator.invoices.updateDraft', invoice._id, {
        recipientLabel: draftTitle,
        dueAt: draftDueDate,
        taxRateBps: Math.round(Number(draftTaxPercent) * 100),
        minHourlyRateMinor: dollarsToMinor(draftMinimumRate),
        currency: draftCurrency,
      });
      noticeMessage = 'Draft invoice saved.';
    } catch (error) {
      errorMessage = error.reason || error.message;
    } finally {
      savingInvoiceDraft = false;
    }
  }

  function clearHourBitEditor() {
    editingHourBitId = '';
    bitLabel = '';
    bitDescription = '';
    bitHours = '';
    bitFixedCost = '';
    bitReported = true;
  }

  function editHourBit(bit) {
    editingHourBitId = bit._id;
    bitLabel = bit.label;
    bitDescription = bit.description || '';
    bitHours = String(bit.hours);
    bitFixedCost = String((bit.fixedCostMinor || 0) / 100);
    bitReported = Boolean(bit.reported);
  }

  async function saveHourBit() {
    if (!invoice || invoice.status !== 'draft' || savingHourBit) return;
    savingHourBit = true;
    errorMessage = '';
    noticeMessage = '';
    const payload = {
      label: bitLabel,
      description: bitDescription,
      hours: Number(bitHours),
      fixedCostMinor: dollarsToMinor(bitFixedCost),
      reported: bitReported,
    };
    try {
      if (editingHourBitId) {
        await Meteor.callAsync('operator.hourBits.updateDraft', editingHourBitId, payload);
        noticeMessage = 'Hour-bit updated.';
      } else {
        await Meteor.callAsync('operator.hourBits.create', invoice._id, payload);
        noticeMessage = 'Hour-bit added.';
      }
      clearHourBitEditor();
    } catch (error) {
      errorMessage = error.reason || error.message;
    } finally {
      savingHourBit = false;
    }
  }

  async function removeHourBit(bit) {
    if (!window.confirm(`Remove “${bit.label}” from this draft?`)) return;
    errorMessage = '';
    noticeMessage = '';
    try {
      await Meteor.callAsync('operator.hourBits.removeDraft', bit._id);
      if (editingHourBitId === bit._id) clearHourBitEditor();
      noticeMessage = 'Hour-bit removed.';
    } catch (error) {
      errorMessage = error.reason || error.message;
    }
  }

  // Reactive, not a bare function call in the template. Svelte cannot see that
  // `payerInvoiceUrl()` reads `invoice`, so the markup evaluated it once at mount
  // — when `invoice` was still null — and the operator's Payer link field stayed
  // empty for the life of the page. Copy link worked, which is why it went unseen.
  $: payerInvoiceUrl = invoice?.codenameToken
    ? `${window.location.origin}/invoices/${invoice.codenameToken}`
    : '';

  async function copyInvoiceLink() {
    try {
      await navigator.clipboard.writeText(payerInvoiceUrl);
      noticeMessage = 'Invoice link copied.';
    } catch {
      errorMessage = 'Copy was unavailable. Select the displayed link and copy it manually.';
    }
  }

  async function shareInvoiceLink() {
    if (!navigator.share) return copyInvoiceLink();
    try {
      await navigator.share({ title: invoice.recipientLabel, url: payerInvoiceUrl });
    } catch (error) {
      if (error.name !== 'AbortError') {
        errorMessage = 'Sharing was unavailable. Use Copy link instead.';
      }
    }
  }

  async function selectAccountTheme(event) {
    if (!operatorAccount || themeSavePending) return;
    const previousThemeId = normalizeThemeId(operatorAccount.themeId);
    const nextThemeId = normalizeThemeId(event.target.value);
    selectedThemeId = nextThemeId;
    pendingThemeId = nextThemeId;
    themeSavePending = true;
    errorMessage = '';
    noticeMessage = '';
    try {
      await Meteor.callAsync(
        'operator.accounts.updateTheme',
        operatorAccount._id,
        nextThemeId,
      );
      noticeMessage = 'Account theme updated for all invoice links.';
    } catch (error) {
      pendingThemeId = null;
      selectedThemeId = previousThemeId;
      errorMessage = error.reason || error.message;
    } finally {
      themeSavePending = false;
    }
  }

  async function selectAccountDefaultCurrency(event) {
    if (!operatorAccount || accountCurrencySavePending) return;
    const previousCurrency = normalizeOperationalCurrency(operatorAccount.defaultCurrency);
    const nextCurrency = event.target.value;
    accountDefaultCurrency = nextCurrency;
    pendingCurrency = nextCurrency;
    accountCurrencySavePending = true;
    errorMessage = '';
    noticeMessage = '';
    try {
      await Meteor.callAsync(
        'operator.accounts.updateDefaultCurrency',
        operatorAccount._id,
        nextCurrency,
      );
      newInvoiceCurrency = nextCurrency;
      noticeMessage = `New invoices now default to ${nextCurrency}.`;
    } catch (error) {
      pendingCurrency = null;
      accountDefaultCurrency = previousCurrency;
      errorMessage = error.reason || error.message;
    } finally {
      accountCurrencySavePending = false;
    }
  }
</script>

<main class="shell" class:payer-route={isPayerRoute}>
  {#if showSiteChrome}
    <UnderConstructionBanner />
  {/if}

  {#if isPayerRoute}
    <p class="payer-brand">{brandEyebrow}</p>
  {:else}
    <nav class="topbar" aria-label="Primary">
      <button type="button" on:click={() => navigate('/')}>Invoices</button>
      <button type="button" on:click={() => navigate('/intro')}>How it works</button>
      <button type="button" on:click={() => navigate('/operator')}>Operator</button>
    </nav>
  {/if}

  {#if showSiteChrome}
    <section class="hero">
      <p class="eyebrow">{brandEyebrow}</p>
      {#if route.name === 'home' && instanceMode === INSTANCE_MODES.SOLO}
        <h1>Open your page.</h1>
        <p>Paste the link or code you were sent.</p>
      {:else}
        <h1>Open work; payable.</h1>
        <p>
          You did the work. Post the hours. Let the people who benefit decide what
          it was worth.
        </p>
      {/if}
    </section>
  {/if}

  <StatusToast message={errorMessage} variant="error" />
  <StatusToast message={noticeMessage} variant="notice" stack={1} />

  {#if routeState === ROUTE_STATES.LOADING}
    <!-- Structural skeleton, never a spinner over the whole page: on the payer
         surface the record's shape is what tells a stranger they are in the
         right place while the rest arrives (APP_STYLE §8). -->
    <RouteSkeleton variant={skeletonVariant} label={loadingMessage} />
  {:else if routeStateTakesOver}
    <RouteState
      {routeKey}
      state={routeState}
      onAction={routeKey === 'operator' ||
        routeKey === 'operatorAccountSettings' ||
        routeKey === 'operatorNewProject' ||
        routeKey === 'operatorProjectSettings'
        ? () => navigate('/operator')
        : routeKey === 'intro'
          ? () => navigate('/intro')
          : () => navigate('/')}
    />
  {:else if route.name === 'intro'}
    <IntroPage />
  {:else if route.name === 'pay'}
    <PaymentAttemptPage
      {paymentAttempt}
      {attemptItems}
      operatorAccount={routeOperatorBranding}
      {checkoutReturnHint}
      {startingNextStep}
      {retryingCheckout}
      onBeginNextStep={beginNextStep}
      onRetryCheckout={retryCheckout}
    />
  <!-- No `&& isOperator` guard: an account that cannot open settings resolved to
       the denied state above, so reaching here means it can. -->
  {:else if routeKey === 'operatorAccountSettings'}
    <OperatorAccountSettingsPage
      {operatorMembership}
      {selectedThemeId}
      {themeSavePending}
      {accountDefaultCurrency}
      {accountCurrencySavePending}
      onSelectTheme={selectAccountTheme}
      onSelectDefaultCurrency={selectAccountDefaultCurrency}
      onBack={() => navigate('/operator')}
    />
  {:else if routeKey === 'operatorNewProject'}
    <OperatorNewProjectPage
      bind:newProjectName
      bind:newProjectCurrency
      bind:newProjectShowPaymentProgressPercent
      {creatingProject}
      onCreateProject={createProject}
      onBack={() => navigate('/operator')}
    />
  {:else if routeKey === 'operatorProjectSettings'}
    <OperatorProjectSettingsPage
      {operatorMembership}
      {settingsProject}
      {instanceMode}
      bind:projectName
      {savingProjectName}
      {savingProjectPolicy}
      onSaveProjectName={saveProjectName}
      {projectCurrency}
      bind:settlementCadence
      bind:processorFeeQuotePolicyId
      bind:transferTax
      bind:showPaymentProgressPercent
      bind:payerFeeShareBps
      bind:operatorFeeShareBps
      onChangeProjectCurrency={selectProjectCurrency}
      onSavePolicy={saveProjectPolicy}
      onBack={returnToProjectDesk}
    />
  {:else if route.name === 'operator'}
    <section class="operator-grid" class:detail-open={isOperator && invoiceDetailOpenOnMobile}>
      <OperatorAccessPanel
        {brandEyebrow}
        {isOperator}
        {currentUser}
        {operationsSummary}
        {instanceMode}
        {projects}
        {selectedProjectId}
        {selectedProject}
        canManageSettings={canManageOperatorAccount}
        {showArchived}
        {selectedInvoiceId}
        {visibleInvoices}
        bind:newInvoiceCurrency
        {creatingInvoice}
        {invoicePage}
        {hasNextInvoicePage}
        bind:loginEmail
        bind:loginPassword
        onSelectProject={selectProject}
        onSetArchiveView={setArchiveView}
        onSelectInvoice={selectInvoice}
        onCreateInvoice={createInvoice}
        onPreviousPage={() => changeInvoicePage(invoicePage - 1)}
        onNextPage={() => changeInvoicePage(invoicePage + 1)}
        onOpenAccountSettings={() => navigate('/operator/account-settings')}
        onCreateProject={() => navigate('/operator/projects/new')}
        onOpenProjectSettings={() => navigate(`/operator/projects/${selectedProjectId}/settings`)}
        onLogout={logoutOperator}
        onLogin={loginOperator}
      />

      <OperatorInvoiceDetail
        {invoice}
        detailReady={operatorInvoiceDetailReady}
        {isOperator}
        {instanceMode}
        {showArchived}
        {hasNoInvoicesHere}
        {invoicePage}
        {creatingInvoice}
        {canCreateInvoice}
        onCreateInvoice={createInvoice}
        {invoiceIssuanceIssues}
        {invoiceReadinessWarnings}
        {operatorExpectedQuote}
        {operatorFeeQuoteAvailable}
        bind:draftTitle
        bind:draftDueDate
        bind:draftTaxPercent
        bind:draftCurrency
        bind:draftMinimumRate
        {savingInvoiceDraft}
        {editingHourBitId}
        bind:bitLabel
        bind:bitHours
        bind:bitFixedCost
        bind:bitDescription
        bind:bitReported
        {savingHourBit}
        payerUrl={payerInvoiceUrl}
        {hourBits}
        {bitRates}
        {attempts}
        {attemptItems}
        {riskObservations}
        onSetInvoiceStatus={setInvoiceStatus}
        onSetArchived={setArchived}
        onSaveDraft={saveInvoiceDraft}
        onSaveBit={saveHourBit}
        onCancelEditBit={clearHourBitEditor}
        onEditBit={editHourBit}
        onRemoveBit={removeHourBit}
        onToggleReported={setReported}
        onCopyLink={copyInvoiceLink}
        onShareLink={shareInvoiceLink}
        onBackToList={() => { invoiceDetailOpenOnMobile = false; }}
      />
    </section>
  {:else if route.name === 'home' && instanceMode === INSTANCE_MODES.SOLO}
    <SoloHomeEntry
      bind:payerLinkInput
      onOpenPayerInvoice={openPayerInvoice}
      onNavigateOperator={() => navigate('/operator')}
    />
  {:else if route.name === 'invoice'}
    <!-- Single column, no operator summary card beside it. The payer route used
         to render InvoiceSummaryCard too, which repeated the invoice identity and
         offered an "Open payer page" button pointing at the current page. -->
    <!-- No `{#if invoice}` guard: an unresolvable token is the route's not-found
         state, handled above. -->
    <PayerInvoiceView
      {invoice}
      operatorAccount={routeOperatorBranding}
      {quote}
      {suggestedQuote}
      {payerQuoteDiffersFromSuggestion}
      {hourBits}
      {bitRates}
      {selectedBitIds}
      bind:hourlyRate
      bind:flatAddition
      bind:flatAdditionReason
      bind:payerEmail
      bind:payerName
      bind:payerCompany
      bind:providerId
      {availableProviders}
      {selectedProvider}
      {payerFeeQuoteAvailable}
      {selectedHours}
      {payerBlockers}
      {ledgerAggregate}
      onToggleBit={toggleBit}
      onCreateIntent={createIntent}
    />
  {:else}
    <section class="invoice-grid">
      <section class="panel">
        <!-- The empty index renders in place rather than replacing the route:
             the invitation belongs where the list would have been. -->
        {#if routeState === ROUTE_STATES.EMPTY}
          <RouteState {routeKey} state={routeState} bare />
        {:else}
          <HomeInvoiceList
            invoices={visibleInvoices}
            activeInvoiceId={invoice?._id}
            page={invoicePage}
            hasNext={hasNextInvoicePage}
            onSelect={selectInvoice}
            onPrevious={() => changeInvoicePage(invoicePage - 1)}
            onNext={() => changeInvoicePage(invoicePage + 1)}
          />
          <InvoiceSummaryCard
            {invoice}
            {reportedBits}
            detailReady={openInvoiceDetailReady}
            onOpenPayerPage={() => navigate(`/invoices/${invoice.codenameToken}`)}
          />
        {/if}
      </section>
    </section>
  {/if}
</main>
